import React, { useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Container,
  Box,
} from "@mui/material";
import { useHistory } from "react-router-dom";

// Import images
import ehrImage from "../../assets/images/gallery/HTMATE_Icon.png";
import bridgeImage from "../../assets/images/gallery/HTBRIDGE_icon.png";
import hrpImage from "../../assets/images/gallery/HTHRP_icon.png";
import HTLIFE_logo from "../../assets/images/gallery/HTLIFE_logo.png";
import digitalTransformationIcon from "../../assets/images/gallery/Digital transformation.png";
import stethoscopeIcon from "../../assets/images/gallery/Stethescope.png";
import hlthtek from "../../assets/images/logo/NavLogo.png";
import abdmLogo from "../../assets/images/gallery/abdm-logo.svg";
import worldTypeImage from "../../assets/images/gallery/World type image.svg";

// Import partner logos
import AuthentechLogo from "../../assets/images/gallery/Authentech Software logo.png";
import ClinicQuestLogo from "../../assets/images/gallery/Clinic Quest logo.png";
import DataMedikLogo from "../../assets/images/gallery/Data medik logo.png";
import LongLifeLogo from "../../assets/images/gallery/Long Life logo.png";
import MobilabLogo from "../../assets/images/gallery/Mobilab logo.png";
import TreatMedLogo from "../../assets/images/gallery/TreatMed logo.png";

const cardData = [
  {
    title: "HTMATE – Electronic Health Record (EHR) System",
    description:
      "HTMATE is a comprehensive Electronic Health Record (EHR) system built to streamline healthcare workflows, empower physicians, and enhance patient care. Designed with cutting-edge technology, it offers a range of features that enable healthcare providers to deliver faster, more accurate care, and patients to enjoy a seamless healthcare experience.",
    image: ehrImage,
    buttonLabel: "Login",
    redirectPath: "/",
  },
  {
    title: "HTBRIDGE – ABDM Connector (No Storage)",
    description:
      "HTBRIDGE is an ABDM-compliant connector that facilitates secure health data sharing without storing patient records on the Hlthtek platform. Designed for healthcare providers seeking a lightweight, compliant solution, HTBRIDGE ensures smooth, real-time data transfer between systems, allowing providers to manage patient information securely without the burden of storage.",
    image: bridgeImage,
    buttonLabel: "Login",
    redirectPath: "/HTBridgeConnector",
  },
  {
    title: "HTHRP – ABDM Connector (With Storage)",
    description:
      "HTHRP is an advanced ABDM-compliant connector that combines the seamless data linking of HTBRIDGE with the added benefit of secure storage on the Hlthtek platform. Ideal for providers who need both real-time health data sharing and secure, long-term storage, HTHRP ensures compliance, data privacy, and easy access to patient records. It's the complete solution for managing health data efficiently within a secure digital ecosystem.",
    image: hrpImage,
    buttonLabel: "Login",
    redirectPath: "/HTHRPHealthRepositoryProvider",
  },
  {
    title: "HTLIFE: Your Personalized Health Companion",
    description:
      "HTLIFE is a revolutionary m-Health application designed to empower you with personalized healthcare at your fingertips. From managing medical records and booking appointments to tracking wellness goals and accessing telemedicine services, HTLIFE puts you in full control of your health journey. Seamlessly connected to your healthcare providers and insurance, it’s your one-stop digital companion for a healthier life.",
    image: HTLIFE_logo,
    buttonLabel: "Login",
    redirectPath: "/htmate",
  },
];

const MainLanding = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const history = useHistory();

  const handleRedirect = (path) => {
    history.push(path);
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Container
      sx={{
        py: 2,
        px: 4,
        maxWidth: {
          xs: "100%",
          sm: "100%",
          md: "900px",
          lg: "1500px",
          xl: "1500px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 5,
          px: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: { xs: 2, md: 0 },
          }}
        >
          <Box
            component="img"
            src={stethoscopeIcon}
            alt="Healthcare"
            sx={{ height: 100, width: "auto" }}
          />
        </Box>

        <Box
          sx={{
            textAlign: { xs: "center", md: "right" },
            marginLeft: { md: "auto" },
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", mb: 3, fontSize: { xs: "h5.fontSize", md: "h4.fontSize" } }}
          >
            DIGITAL TRANSFORMATION
          </Typography>
          <Box
            component="img"
            src={digitalTransformationIcon}
            alt="Digital Transformation"
            sx={{
              width: {
                xs: "100%",
                sm: "80%",
                md: "70%",
                lg: "60%",
                xl: "50%",
              },
              maxWidth: "800px",
              height: "auto",
              mt: 2,
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          height: { xs: "300px", md: "400px" },
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 3,
        }}
      >
        <img
          src={worldTypeImage}
          alt="World Type Background"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: "auto",
            zIndex: -1,
          }}
        />

        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 2,
            fontSize: { xs: "h4.fontSize", md: "h3.fontSize" },
          }}
        >
          Building India’s Connected Healthcare Future
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            mb: 4,
            maxWidth: "500px",
            fontSize: { xs: "body1.fontSize", md: "h6.fontSize" },
          }}
        >
          Empowering healthier lives through a seamless digital ecosystem that
          connects patients, doctors, and insurers under one unified platform.
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2, mb: { xs: 2, md: 0 } }}
          >
            Get Started
          </Button>
          <Button variant="outlined" color="primary">
            How it works?
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 5,
          mb: 5,
          px: 3,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 2, md: 0 },
          }}
        >
          <Box
            component="img"
            src="https://via.placeholder.com/50"
            alt="Reviewer 1"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              overflow: "hidden",
              mr: 1,
            }}
          />
          <Box
            component="img"
            src="https://via.placeholder.com/50"
            alt="Reviewer 2"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              overflow: "hidden",
              mr: 1,
            }}
          />
          <Box
            component="img"
            src="https://via.placeholder.com/50"
            alt="Reviewer 3"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              overflow: "hidden",
              mr: 1,
            }}
          />
          <Typography variant="body1" sx={{ fontWeight: "bold", ml: 1 }}>
            +10k Reviewers
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={hlthtek}
            alt="HLHTEK Logo"
            sx={{
              height: 50,
              mr: 3,
            }}
          />
          <Box
            component="img"
            src={abdmLogo}
            alt="ABDM Logo"
            sx={{
              height: 50,
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 5,
          mb: 5,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mr: { md: 2 }, mb: { xs: 2, md: 0 } }}
          >
            Trusted by 50+ Organisations
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderLeft: { md: "1px solid #ddd" },
              pl: { md: 2 },
              flexWrap: "wrap",
            }}
          >
            <Box
              component="img"
              src={LongLifeLogo}
              alt="Long Life Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={MobilabLogo}
              alt="Mobilab Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={AuthentechLogo}
              alt="Authentech Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={ClinicQuestLogo}
              alt="Clinic Quest Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={DataMedikLogo}
              alt="DataMedik Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={TreatMedLogo}
              alt="TreatMed Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
          </Box>
        </Box>

        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            width: "100%",
            maxWidth: "800px",
            textAlign: "center",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              50+
            </Typography>
            <Typography variant="body2">Partners</Typography>
          </Box>
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              100+
            </Typography>
            <Typography variant="body2">Customers</Typography>
          </Box>
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
          <Box
            component="img"
            src={MobilabLogo}
              alt="Mobilab Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={AuthentechLogo}
              alt="Authentech Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={ClinicQuestLogo}
              alt="Clinic Quest Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={DataMedikLogo}
              alt="DataMedik Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
            <Box
              component="img"
              src={TreatMedLogo}
              alt="TreatMed Logo"
              sx={{ height: 40, mx: 1, mb: { xs: 2, md: 0 } }}
            />
          </Box>
        </Box> */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            width: "100%",
            maxWidth: "800px",
            textAlign: "center",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              50+
            </Typography>
            <Typography variant="body2">Partners</Typography>
          </Box>
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              100+
            </Typography>
            <Typography variant="body2">Customers</Typography>
          </Box>
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              1000+
            </Typography>
            <Typography variant="body2">Monthly users</Typography>
          </Box>
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              2000+
            </Typography>
            <Typography variant="body2">Daily queries</Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          textAlign: "center",
          mt: 5,
          mb: 5,
          px: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Featured Products
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: "1000px", margin: "0 auto" }}
        >
          HLHTEK offers a suite of cutting-edge digital healthcare products
          designed to streamline data sharing, enhance patient care, and empower
          providers with secure, compliant solutions. From seamless health data
          connectors to personalized m-Health applications, HLHTEK is
          transforming the healthcare landscape with innovative technology that
          bridges gaps and simplifies care delivery.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {cardData.map((card, index) => (
          <Grid item key={index} xs={12} sm={6} md={6} lg={6}>
            <Box sx={{ px: { xs: 2, sm: 4, md: 10 } }}>
              <Card
                sx={{
                  height: "100%",
                  minHeight: "550px",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: 8,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    boxShadow: 16,
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Box
                  sx={{
                    height: 250,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={card.image}
                    alt={card.title}
                    sx={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, paddingBottom: 2 }}>
                  <Typography gutterBottom variant="h6" component="h2">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
                <Button
                  size="small"
                  sx={{
                    m: 2,
                    backgroundColor: "#21cdc0",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#1bb3a7" },
                  }}
                  variant="contained"
                  onClick={() => handleRedirect(card.redirectPath)}
                >
                  {card.buttonLabel}
                </Button>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default MainLanding;

 

 

